// src/components/ProcurementOverview.tsx
// G17 / R07 — Procurement command centre (Sprint 3 default tab)
// Cross-job KPI dashboard + per-job health table with drill-down.

import { MOCK_JOBS, MOCK_POS } from '../utils/mockData'
import { MOCK_INVOICES } from '../utils/mockDataInvoices'
import { MOCK_GRNS, poNeedsGRN } from '../utils/mockDataGRN'
import { getComplianceIssueCount } from '../utils/supplierCompliance'
import { formatCurrency } from '../utils/formatters'
import { AlertTriangle, CheckCircle, Clock, Package, FileText, Shield } from 'lucide-react'

interface ProcurementOverviewProps {
  onJobSelect: (jobId: string) => void
}

export function ProcurementOverview({ onJobSelect }: ProcurementOverviewProps) {
  const activeJobs = MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status))
  const allPOs     = MOCK_POS as any[]
  const now        = new Date()

  const totalCommitment  = allPOs.filter(p => !['DELIVERED','REJECTED'].includes(p.status)).reduce((s, p) => s + p.totalExGST, 0)
  const totalBudget      = activeJobs.reduce((s, j) => s + j.contractValue, 0)
  const commitmentPct    = totalBudget > 0 ? Math.round(totalCommitment / totalBudget * 100) : 0
  const pendingApprovals = allPOs.filter(p => p.status === 'PENDING_APPROVAL').length
  const overdueDeliveries= allPOs.filter(p => ['APPROVED','SENT'].includes(p.status) && p.deliveryDate && new Date(p.deliveryDate) < now).length
  const unmatchedInvoices= MOCK_INVOICES.filter(i => i.status === 'UNMATCHED').length
  const unconfirmedGRNs  = allPOs.filter(p => poNeedsGRN(p.status) && !MOCK_GRNS.some((g: any) => g.poRef === (p.poNumber ?? p.id))).length
  const suppliersWithIssues = getComplianceIssueCount()

  const kpis = [
    { label: 'Total commitment',   value: formatCurrency(totalCommitment), sub: `${commitmentPct}% of combined active budget`, color: commitmentPct >= 95 ? 'text-red-600' : commitmentPct >= 80 ? 'text-amber-600' : 'text-gray-900', border: commitmentPct >= 95 ? 'border-red-200 bg-red-50' : commitmentPct >= 80 ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white', icon: <Package className="w-4 h-4" /> },
    { label: 'Pending approvals',  value: pendingApprovals,                sub: 'POs awaiting PM sign-off',                    color: pendingApprovals > 0 ? 'text-amber-600' : 'text-gray-400',   border: pendingApprovals > 0 ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white',     icon: <Clock className="w-4 h-4" /> },
    { label: 'Overdue deliveries', value: overdueDeliveries,               sub: 'Past expected delivery date',                 color: overdueDeliveries > 0 ? 'text-red-600' : 'text-gray-400',    border: overdueDeliveries > 0 ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white',       icon: <AlertTriangle className="w-4 h-4" /> },
    { label: 'Unlinked invoices',  value: unmatchedInvoices,               sub: 'No PO or job reference attached',             color: unmatchedInvoices > 0 ? 'text-orange-600' : 'text-gray-400', border: unmatchedInvoices > 0 ? 'border-orange-200 bg-orange-50' : 'border-gray-200 bg-white', icon: <FileText className="w-4 h-4" /> },
    { label: 'Unconfirmed GRNs',   value: unconfirmedGRNs,                 sub: 'POs awaiting delivery confirmation',          color: unconfirmedGRNs > 0 ? 'text-blue-600' : 'text-gray-400',     border: unconfirmedGRNs > 0 ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white',       icon: <CheckCircle className="w-4 h-4" /> },
    { label: 'Supplier compliance',value: suppliersWithIssues > 0 ? `${suppliersWithIssues} issue${suppliersWithIssues !== 1 ? 's' : ''}` : 'All clear', sub: suppliersWithIssues > 0 ? 'Expiring or expired credentials' : 'No credentials expiring within 60 days', color: suppliersWithIssues > 0 ? 'text-red-600' : 'text-green-700', border: suppliersWithIssues > 0 ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50', icon: <Shield className="w-4 h-4" /> },
  ]

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-3">
        {kpis.map(kpi => (
          <div key={kpi.label} className={`border rounded-xl p-4 ${kpi.border}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className={`text-2xl font-bold tabular-nums ${kpi.color}`}>{kpi.value}</div>
                <div className="text-xs font-medium text-gray-800 mt-0.5">{kpi.label}</div>
                <div className="text-xs text-gray-500">{kpi.sub}</div>
              </div>
              <div className={`${kpi.color} opacity-50 mt-0.5`}>{kpi.icon}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b bg-gray-50 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-gray-900">Per-job procurement health</div>
            <div className="text-xs text-gray-500 mt-0.5">Click a row to open that job in Purchase Orders</div>
          </div>
          <div className="text-xs text-gray-400">{activeJobs.length} active job{activeJobs.length !== 1 ? 's' : ''}</div>
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-gray-50 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
              <th className="text-left px-4 py-2.5">Job</th>
              <th className="text-left px-3 py-2.5">Description</th>
              <th className="text-right px-3 py-2.5">Contract</th>
              <th className="text-right px-3 py-2.5">Committed</th>
              <th className="text-center px-3 py-2.5">%</th>
              <th className="text-center px-3 py-2.5">Pending</th>
              <th className="text-center px-3 py-2.5">Overdue</th>
              <th className="text-center px-3 py-2.5">Health</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {activeJobs.map(job => {
              const jobPOs    = allPOs.filter(p => p.jobId === job.id)
              const committed = jobPOs.filter(p => !['DELIVERED','REJECTED'].includes(p.status)).reduce((s, p) => s + p.totalExGST, 0)
              const pct       = job.contractValue > 0 ? Math.round(committed / job.contractValue * 100) : 0
              const pending   = jobPOs.filter(p => p.status === 'PENDING_APPROVAL').length
              const overdue   = jobPOs.filter(p => ['APPROVED','SENT'].includes(p.status) && p.deliveryDate && new Date(p.deliveryDate) < now).length
              const health    = pct >= 95 ? 'CRITICAL' : pct >= 80 ? 'WARN' : 'OK'
              return (
                <tr key={job.id} onClick={() => onJobSelect(job.id)} className="cursor-pointer hover:bg-blue-50 transition-colors">
                  <td className="px-4 py-2.5 font-mono font-semibold text-gray-900">{job.jobNumber}</td>
                  <td className="px-3 py-2.5 text-gray-700 max-w-xs truncate">{job.description}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-gray-500">{formatCurrency(job.contractValue)}</td>
                  <td className={`px-3 py-2.5 text-right tabular-nums font-semibold ${health === 'CRITICAL' ? 'text-red-600' : health === 'WARN' ? 'text-amber-600' : 'text-gray-700'}`}>{formatCurrency(committed)}</td>
                  <td className={`px-3 py-2.5 text-center font-bold ${pct >= 95 ? 'text-red-600' : pct >= 80 ? 'text-amber-600' : 'text-gray-600'}`}>{pct}%</td>
                  <td className="px-3 py-2.5 text-center">{pending > 0 ? <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold">{pending}</span> : <span className="text-gray-300">—</span>}</td>
                  <td className="px-3 py-2.5 text-center">{overdue > 0 ? <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-bold">{overdue}</span> : <span className="text-gray-300">—</span>}</td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`px-2 py-0.5 rounded-full font-semibold text-[10px] ${health === 'CRITICAL' ? 'bg-red-100 text-red-700' : health === 'WARN' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                      {health === 'CRITICAL' ? 'Near limit' : health === 'WARN' ? 'Watch' : 'Healthy'}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {suppliersWithIssues > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-3 text-sm text-red-800">
          <Shield className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div><span className="font-semibold">{suppliersWithIssues} supplier{suppliersWithIssues !== 1 ? 's' : ''}</span>{' '}{suppliersWithIssues !== 1 ? 'have' : 'has'} expiring or expired compliance credentials. Review the Suppliers module before placing new orders.</div>
        </div>
      )}
    </div>
  )
}
